import React, { useMemo, useState } from 'react'
import { useCohort, useUpdateCohort } from '@/hooks/useCohorts'
import { Button, ButtonGroup, Card, CardContent, Skeleton, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Dialog, DialogContent, DialogHeader, DialogTitle } from '@duck/ui'
import { useTar } from '@/modules/i18n'
import { usePartnerTar } from '@/hooks/usePartnerTar'
import { CohortPeopleList } from './People/CohortPeopleList'
import { buildCohortPageData } from './cohortPageData'
import { usePersons } from '@/hooks/usePersons'
import { useTaskDisplayNames } from '@/modules/entityLookups'
import { AssignmentsList } from './Assignments/AssignmentsList'
import { PublicAssignmentsList } from './Assignments/PublicAssignmentsList'
import { SendAssignment } from './Assignments/SendAssignment'
import { AddCohortPersonDialog } from './People/AddCohortPersonDialog'
import { CreateCohortDialog } from '@/components/PartnerCohorts/CreateCohortDialog'
import { DeleteDialog } from '@/components/DeleteDialog/DeleteDialog'
import { Settings, PencilIcon, X, ChevronDown, Zap, UserPlus, Mail, Link as LinkIcon, RotateCcw } from 'lucide-react'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { ErrorState } from '@/components/EmptyStates'
import { useAuth } from '@/hooks/useAuth'
import { PartnerService } from '@duck/api-definitions'
import { useOrganizationAdminSettings, useOrganizationSettings } from '@/hooks/useOrganizationSettings'
import { toast } from 'sonner'

interface CohortPageProps {
  cohortId: string
}

export const CohortPage: React.FC<CohortPageProps> = ({ cohortId }) => {
  const { cohort, assignmentsDetails, publicAssignments, isLoading, isError, error, refetch } = useCohort(cohortId)
  const { persons, isLoading: isLoadingPersons } = usePersons(cohort?.personIds ?? [])
  const { getOrgSetting } = useOrganizationSettings()
  const { getOrgAdminSetting } = useOrganizationAdminSettings()
  const { allPersons, taskAssignments } = useMemo(() => {
    if (!cohort || !assignmentsDetails || !persons || persons.length === 0) {
      return { allPersons: [], taskAssignments: {} }
    }
    return buildCohortPageData(cohort, assignmentsDetails, persons)
  }, [cohort, assignmentsDetails, persons])
  const taskIds = useMemo(() => Object.keys(taskAssignments), [taskAssignments])
  const { getDisplayName } = useTaskDisplayNames(taskIds)

  const assignmentCountByPersonId = useMemo(() => {
    const map: Record<string, number> = {}
    for (const d of assignmentsDetails ?? []) {
      map[d.personId] = (map[d.personId] ?? 0) + 1
    }
    return map
  }, [assignmentsDetails])

  const isAnythingLoading = isLoading || isLoadingPersons

  const { t } = useTar()
  const { tP } = usePartnerTar()
  const { organizationId } = useAuth()
  const updateCohort = useUpdateCohort(cohortId, organizationId ?? undefined)
  const [view, setView] = useState<'all' | 'assignments' | 'publicAssignments'>('all')
  const [isAddPeopleDialogOpen, setIsAddPeopleDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false)
  const [isSendAssignmentDialogOpen, setIsSendAssignmentDialogOpen] = useState(false)
  const [isPublicAssignmentDialogOpen, setIsPublicAssignmentDialogOpen] = useState(false)
  const [checkedPersonIds, setCheckedPersonIds] = useState<Set<string>>(new Set())

  // Calculate open assignments count from assignmentsDetails
  const openAssignmentsCount = useMemo(() => {
    if (!assignmentsDetails || assignmentsDetails.length === 0) return 0

    // Group assignmentsDetails by assignmentId
    const assignmentsByAssignmentId = assignmentsDetails.reduce((acc, detail) => {
      if (!acc[detail.assignmentId]) {
        acc[detail.assignmentId] = []
      }
      acc[detail.assignmentId].push(detail)
      return acc
    }, {} as Record<string, typeof assignmentsDetails>)

    // Count assignments where submissions < personIds.length
    let openCount = 0
    for (const assignmentId in assignmentsByAssignmentId) {
      const details = assignmentsByAssignmentId[assignmentId]
      const submissions = details.filter(d => d.status === PartnerService.AssignmentDetailsStatus.Submitted).length
      const totalPersons = details.length

      if (submissions < totalPersons) {
        openCount++
      }
    }

    return openCount
  }, [assignmentsDetails])

  // Build warning message if there are open assignments
  const openAssignmentsWarning = useMemo(() => {
    if (openAssignmentsCount > 0) {
      return tP('openAssignmentsWarning', { count: openAssignmentsCount })
    }
    return undefined
  }, [openAssignmentsCount, tP])

  const invitationEmails = getOrgSetting('invitationEmails') ?? false
  const allowPublicLinks = getOrgAdminSetting('allowPublicLinks') ?? false

  if (isError || (!isLoading && !cohort)) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {tP('cohort')}
          </h1>
        </div>
        <Card>
          <CardContent className="pt-6">
            {error ? (
              <ErrorState
                title={t('common.errorLoading', { item: tP('cohort').toLowerCase() })}
                description={t('common.errorLoadingDescription')}
                onRetry={() => refetch()}
              />
            ) : (
              <ErrorState
                icon={MagnifyingGlassIcon}
                title={t('common.notFound', { item: tP('cohort') })}
                description={t('common.notFoundDescription')}
              />
            )}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {isAnythingLoading || !cohort ? (
            <>
              <Skeleton className="h-9 w-1/3" />
              <Skeleton className="h-4 w-1/2 mt-2" />
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold text-foreground">
                {cohort.name}
                {!cohort.isActive && (
                  <span className="text-muted-foreground"> ({t('partner.closed')})</span>
                )}
              </h1>
              {cohort.description && (
                <p className="mt-2 text-sm text-muted-foreground">{cohort.description}</p>
              )}
            </>
          )}
        </div>
        {!isAnythingLoading && cohort && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => {
                setIsEditDialogOpen(true)
              }}>
                <PencilIcon className="h-4 w-4 me-2" />
                {tP('editDetails')}
              </DropdownMenuItem>
              {cohort.isActive ? (
                <DropdownMenuItem onClick={() => setIsCloseDialogOpen(true)}>
                  <X className="h-4 w-4 me-2" />
                  {tP('closeCohort')}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={async () => {
                    try {
                      await updateCohort.mutateAsync({
                        name: cohort.name,
                        description: cohort.description,
                        organizationId: cohort.organizationId,
                        type: cohort.type,
                        isActive: true,
                        personIds: cohort.personIds,
                      })
                      toast.success(tP('cohortReopened'))
                    } catch (e) {
                      toast.error(t('errors.somethingWentWrongWeveBeenNotified'))
                    }
                  }}
                >
                  <RotateCcw className="h-4 w-4 me-2" />
                  {tP('reopenCohort')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className="flex flex-row flex-wrap gap-2 justify-between">
        <ButtonGroup className="flex-nowrap">
          <Button
            variant="outline"
            className={`h-8 px-2 text-xs md:h-10 md:px-4 md:text-sm ${view === 'all' ? 'bg-primary/60' : ''}`}
            onClick={() => setView('all')}

          >
            {tP('people')}
          </Button>
          <Button
            variant="outline"
            className={`h-8 px-2 text-xs md:h-10 md:px-4 md:text-sm ${view === 'assignments' ? 'bg-primary/60' : ''}`}
            onClick={() => {
              if (view === 'all') {
                setCheckedPersonIds(new Set())
              }
              setView('assignments')
            }}

          >
            {invitationEmails ? t('partner.sentAssignments') : t('partner.assignments')}
          </Button>
          {allowPublicLinks && <Button
            variant="outline"
            className={`h-8 px-2 text-xs md:h-10 md:px-4 md:text-sm ${view === 'publicAssignments' ? 'bg-primary/60' : ''}`}
            onClick={() => {
              if (view === 'all') {
                setCheckedPersonIds(new Set())
              }
              setView('publicAssignments')
            }}
          >
            {t('partner.publicAssignments')}
          </Button>}
        </ButtonGroup>
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={!cohort?.isActive}>
            <Button
              variant="default"
              className="gap-2 bg-primary/80 text-foreground"
              disabled={!cohort?.isActive}
              title={!cohort?.isActive ? tP('cohortClosedActionsDisabled') : undefined}
            >
              <Zap className="h-4 w-4 shrink-0" aria-hidden />
              {t('partner.actions')}
              <ChevronDown className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[240px]">
            <DropdownMenuItem
              className="flex flex-col items-start gap-0.5 py-2.5 cursor-pointer focus:bg-muted/70 focus:text-foreground"
              onClick={() => setIsAddPeopleDialogOpen(true)}
            >
              <div className="flex items-center gap-2 w-full">
                <UserPlus className="h-4 w-4 shrink-0" />
                <span className="font-medium">{tP('addPeople')}</span>
              </div>
              <span className="text-xs text-muted-foreground ps-6">{tP('actionsAddPeopleDescription')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex flex-col items-start gap-0.5 py-2.5 cursor-pointer focus:bg-muted/70 focus:text-foreground"
              onClick={() => {
                if (checkedPersonIds.size === 0) {
                  setView('all')
                  toast.error(tP('pleaseSelectPeople'))
                  return
                }
                setIsSendAssignmentDialogOpen(true)
              }}
            >
              <div className="flex items-center gap-2 w-full">
                <Mail className="h-4 w-4 shrink-0" />
                <span className="font-medium">{t('partner.createAssignment')}</span>
              </div>
              <span className="text-xs text-muted-foreground ps-6">{tP('actionsNewAssignmentDescription')}</span>
            </DropdownMenuItem>
            {allowPublicLinks && <DropdownMenuItem
              className="flex flex-col items-start gap-0.5 py-2.5 cursor-pointer focus:bg-muted/70 focus:text-foreground"
              onClick={() => setIsPublicAssignmentDialogOpen(true)}
            >
              <div className="flex items-center gap-2 w-full">
                <LinkIcon className="h-4 w-4 shrink-0" />
                <span className="font-medium">{t('partner.createPublicAssignment')}</span>
              </div>
              <span className="text-xs text-muted-foreground ps-6">{t('partner.actionsCreatePublicDescription')}</span>
            </DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isAnythingLoading ? (
        view === 'assignments' || view === 'publicAssignments' ? (
          <AssignmentsList
            assignmentsDetails={[]}
            onAssignmentChange={refetch}
            skeleton={true}
          />
        ) : (
          <CohortPeopleList skeleton={true} listTitle={tP('people')} persons={persons} peopleDetails={[]} cohortId={cohortId} handleCohortDataChange={refetch} />
        )
      ) : view === 'all' ? (
        <CohortPeopleList
          key={cohortId}
          listTitle={tP('people')}
          persons={persons}
          peopleDetails={allPersons}
          cohortId={cohortId}
          handleCohortDataChange={refetch}
          onAddPeople={() => setIsAddPeopleDialogOpen(true)}
          onCheckedPersonIdsChange={setCheckedPersonIds}
          assignmentCountByPersonId={assignmentCountByPersonId}
        />
      ) : view === 'assignments' ? (
        <AssignmentsList
          assignmentsDetails={assignmentsDetails}
          onAssignmentChange={refetch}
        />
      ) : view === 'publicAssignments' ? (
        <PublicAssignmentsList
          publicAssignments={publicAssignments ?? []}
          assignmentsDetails={assignmentsDetails ?? []}
          onAssignmentChange={refetch}
          onCreatePublicAssignment={
            allowPublicLinks && cohort?.isActive
              ? () => setIsPublicAssignmentDialogOpen(true)
              : undefined
          }
        />
      ) : (
        <CohortPeopleList
          listTitle={t('partner.sentAssignments', { taskName: getDisplayName(view) })}
          persons={persons}
          peopleDetails={taskAssignments[view] ?? []}
          cohortId={cohortId}
          handleCohortDataChange={refetch}
          listMode="task"
        />
      )}


      <AddCohortPersonDialog
        isOpen={isAddPeopleDialogOpen}
        onClose={() => setIsAddPeopleDialogOpen(false)}
        cohortId={cohortId}
        onPersonAdded={() => {
          setView('all')
          refetch()
        }}
        onAssignmentSent={() => {
          setView('assignments')
          refetch()
        }}
      />
      <Dialog
        open={isSendAssignmentDialogOpen}
        onOpenChange={setIsSendAssignmentDialogOpen}
      >
        <DialogContent width="sm">
          <DialogHeader>
            <DialogTitle>{invitationEmails ? t('partner.sendAssignment') : t('partner.newAssignment')}</DialogTitle>
          </DialogHeader>
          <SendAssignment
            cohortId={cohortId}
            personIds={Array.from(checkedPersonIds)}
            publicMode={false}
            sendInvitationEmails={invitationEmails}
            onClose={() => setIsSendAssignmentDialogOpen(false)}
            onSend={() => {
              setIsSendAssignmentDialogOpen(false)
              setCheckedPersonIds(new Set())
              setView('assignments')
              refetch()
            }}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        open={isPublicAssignmentDialogOpen}
        onOpenChange={setIsPublicAssignmentDialogOpen}
      >
        <DialogContent width="sm">
          <DialogHeader>
            <DialogTitle>{t('partner.createPublicAssignment')}</DialogTitle>
          </DialogHeader>
          <SendAssignment
            cohortId={cohortId}
            publicMode
            onClose={() => setIsPublicAssignmentDialogOpen(false)}
            onSend={() => {
              setIsPublicAssignmentDialogOpen(false)
              setView('publicAssignments')
              refetch()
            }}
          />
        </DialogContent>
      </Dialog>
      <CreateCohortDialog
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        cohort={cohort || null}
        onCohortUpdated={() => {
          refetch()
        }}
      />
      <DeleteDialog
        open={isCloseDialogOpen}
        onOpenChange={setIsCloseDialogOpen}
        title={tP('closeCohort')}
        description={tP('closeCohortDescription')}
        deleteButtonText={tP('closeCohort')}
        warning={openAssignmentsWarning}
        onDelete={async () => {
          if (!cohort) {
            throw new Error('Cohort is missing')
          }
          await updateCohort.mutateAsync({
            name: cohort.name,
            description: cohort.description,
            organizationId: cohort.organizationId,
            type: cohort.type,
            isActive: false,
            personIds: cohort.personIds,
          })
        }}
      />
    </div>
  )
}
